import { Group } from '@visx/group'
import cx from 'classnames'
import { type MouseEventHandler, useState } from 'react'

import { TextRectangle } from '../shapes/TextRectangle'
import { TEXT_BOX_PADDING_X, TEXT_BOX_PADDING_Y } from './constants'

type Props = {
  x: number
  y: number
  text: string
  isActive?: boolean
  isCurrent?: boolean
  onClick?: () => void
  onMouseOver?: MouseEventHandler<SVGRectElement>
  onMouseOut?: MouseEventHandler<SVGRectElement>
  isMissing?: boolean
}

/** Renders a node from the tree given a D3 TreeNode. The component contains the required logic for measuring the node's
 * text and calculating the bounding rectangle's dimensions based on the text size.
 */
export const InheritanceTreeNode = ({
  x,
  y,
  text,
  isActive,
  isCurrent,
  onClick,
  onMouseOver,
  onMouseOut,
  isMissing = false,
}: Props) => {
  const [rectangleDimensions, setRectangleDimensions] = useState<{
    width: number
    height: number
  }>({ width: 0, height: 0 })

  const handleTextMounted = (textRectangleRef?: SVGGElement | null) => {
    if (textRectangleRef) {
      const { width, height } = textRectangleRef
        ? textRectangleRef.getBoundingClientRect()
        : { width: 0, height: 0 }

      if (
        rectangleDimensions.width !== width ||
        rectangleDimensions.height !== height
      ) {
        setRectangleDimensions({ width, height })
      }
    }
  }

  return (
    <Group top={y} left={x}>
      <TextRectangle
        horizontalPadding={TEXT_BOX_PADDING_X}
        verticalPadding={TEXT_BOX_PADDING_Y}
        text={text}
        rectClassName={cx({
          'cursor-pointer': !!onClick,
          'fill-inheritanceActive': isActive,
          'fill-inheritance': !isActive,
          'opacity-30': isMissing,
          'stroke-inheritance stroke-3': isCurrent,
        })}
        onClick={onClick}
        rounded="md"
        ref={handleTextMounted}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      >
        {({ width, height }) => (
          <text
            x={width / 2}
            y={height / 2}
            dominantBaseline="middle"
            textAnchor="middle"
            className="pointer-events-none fill-amaze-inkBright"
            fontWeight={isCurrent ? 'bolder' : 'normal'}
          >
            {text}
          </text>
        )}
      </TextRectangle>
    </Group>
  )
}
